import { accountSettingQueryOptions } from "app/pages/sales-intelligence/query/options";
import { accountSettingKeyMap } from "app/pages/sales-intelligence/services/api/account-settings/types";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { errorOperationMap } from "app/pages/sales-intelligence/utils/errors/context";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";
import type { ResponseError } from "app/services/fetchService";
import queryClient from "single-spa/react-app/query-client";

/**
 * Resolves the settings that decide *which* UI exists before any SI page renders, so no consumer needs a
 * loading branch for them. `onEnter` on the abstract SI root is awaited by UIRouter (its event type is not
 * synchronous), so this blocks the transition — deliberately: the industry opt-in picks between two different
 * filter cards, and a card that renders and is then swapped is worse than one that arrives a beat later.
 *
 * The cost is bounded. SI transitions already await `swSettings` (`router-on-before`) and the quota check
 * (`router-on-start`), so this joins an existing barrier rather than creating one; the root is abstract, so it
 * runs once per module entry and `ensureQueryData` makes re-entry a cache hit.
 *
 * MUST NOT reject: UIRouter fails the transition on a rejected hook. A failed read settles to the new
 * taxonomy unless the account explicitly opted out (`false`).
 *
 * A 403 is that degradation working, not a fault, so it is not reported: the SI root is also the parent of
 * account review and ad intelligence, so callers with no sales-api entitlement reach here and the API refuses
 * them by design. Everything else (5xx, network, timeout) is a real failure and still reports.
 */
export const onEnterSalesIntelligenceRoot = async (): Promise<void> => {
    // Apps-only never mounts a filter panel or a company card, so the opt-in cannot change anything it renders.
    // Reading the flag here is safe: `onEnter` runs at transition time, after `onBefore` has awaited swSettings.
    if (salesSettingsHelper.isAppsOnly) {
        return;
    }

    try {
        await queryClient.ensureQueryData(
            accountSettingQueryOptions(accountSettingKeyMap.accountOptedInIndustryTaxonomy),
        );
    } catch (e) {
        if ((e as ResponseError)?.status === 403) {
            return;
        }

        trackError(
            new SiError(
                "Failed to resolve the industry taxonomy opt-in on SI entry",
                { category: errorCategoryMap.api, operation: errorOperationMap.resolveIndustryTaxonomyOptIn },
                { cause: e },
            ),
        );
    }
};
